import React, { type Ref } from 'react'
import Icon from '@components/utils/Icon'
import cn from '@components/utils/cn'
import type { IconTypes } from '../utils/Icon'
import { type ButtonStyleProps, rankColorClasses, sizeClasses } from './ButtonStyles'
import { type InertiaLinkProps, Link as InertiaLink } from '@inertiajs/react'
import { ModalLink } from '@inertiaui/modal-react'

type LinkAs = 'a' | 'inertiaLink' | 'modalLink'

export type LinkProps = Omit<InertiaLinkProps, keyof ButtonStyleProps | 'as'> &
  ButtonStyleProps & {
    children: React.ReactNode
    trailingIcon?: IconTypes
    leadingIcon?: IconTypes
    linkBackground?: boolean
    href: string
    ariaLabel?: string
    as?: LinkAs
    navigate?: boolean
    dataCompId?: string
    ref?: Ref<HTMLAnchorElement>
  }

const Link = ({
  size = 'md',
  rank = 'primary',
  color = 'primary',
  trailingIcon,
  leadingIcon,
  linkBackground = true,
  children,
  href,
  ariaLabel,
  as: asProp,
  navigate,
  disabled,
  dataCompId,
  only,
  ref,
  className = '',
  ...props
}: LinkProps) => {
  const resolvedAs: LinkAs = asProp ?? 'a'

  const Element = disabled
    ? 'a'
    : resolvedAs === 'modalLink'
      ? ModalLink
      : resolvedAs === 'inertiaLink'
        ? InertiaLink
        : 'a'

  const extraProps =
    resolvedAs === 'inertiaLink' && !disabled
      ? { only }
      : resolvedAs === 'modalLink' && !disabled
        ? {
            navigate: navigate ?? true,
          }
        : {}

  // https://a11y-guidelines.orange.com/en/articles/disable-elements/
  const disabledLinkProps = {
    role: 'link',
    href: undefined,
    disabled: true,
    'aria-disabled': 'true',
    tabIndex: -1,
  }
  const linkProps = (disabled ?? !href) ? disabledLinkProps : { href }

  return (
    <Element
      ref={ref}
      aria-label={ariaLabel}
      // Element is polymorphic (a | InertiaLink | ModalLink) — TypeScript can't
      // narrow the union. The rest props are standard HTML attributes after
      // destructuring out ButtonStyleProps, as, and navigate.
      {...(props as any)}
      {...extraProps}
      {...linkProps}
      className={cn(
        'tw:rounded-lg tw:transition',
        'tw:inline-flex tw:items-center tw:gap-2',
        disabled
          ? 'tw:aria-disabled:cursor-not-allowed'
          : 'tw:focus-visible:border-primary-500 tw:focus-visible:ring-4 tw:focus-visible:ring-primary-300',
        sizeClasses(size),
        rankColorClasses(rank, color, linkBackground),
        className
      )}
      {...(dataCompId && { 'data-compid': `${dataCompId}-link` })}
    >
      {leadingIcon && <Icon dataTestId="leading-icon" type={leadingIcon} className="tw:h-5 tw:w-5 tw:mt-0.5" />}
      <span className="tw:gap-2 tw:md:whitespace-nowrap tw:flex tw:flex-nowrap tw:items-center">{children}</span>
      {trailingIcon && <Icon dataTestId="trailing-icon" type={trailingIcon} className="tw:h-5 tw:w-5 tw:mt-0.5" />}
    </Element>
  )
}

export default Link
